/**
 * Agency roster — the agent's book of athletes.
 *
 * Reads `agency.listRoster` (athletes + per-athlete deal aggregates) and
 * writes through `organization.invite` / `organization.removeMember`.
 * Those endpoints already handle stub-user creation for brand-new
 * emails, the magic-link invitation, and the resend decision tree, so
 * this page deliberately does not reimplement any of it.
 *
 * Invite is an inline expanding form rather than a modal — @app/ui has
 * no Dialog primitive (only AlertDialog, which is for confirmations),
 * and this matches the inline-reject pattern on compliance/verifications.
 *
 * Soft cap: past the plan's athlete count we show an upsell banner but
 * never block the invite (founder ruling 2026-06-20 — a wall at athlete
 * #11 is the worst possible moment to interrupt a working agent).
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  toast,
} from '@app/ui';
import { formatCurrency, formatDate } from '@app/validators';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Fragment, useState } from 'react';
import { api } from '../../../lib/api';
import { formatMoneyCompact, formatReach } from '../../../lib/format-reach';

/** Mirrors ROSTER_SOFT_CAPS in agency.router.ts. */
const SOFT_CAPS: Record<string, number> = {
  free: 3,
  pro: 3,
  'agent-solo': 10,
  'agent-team': 50,
};

export const Route = createFileRoute('/_authenticated/agency/roster')({
  loader: async ({ context }) => {
    const { getServerApi } = await import('../../../lib/api-server');
    const serverApi = await getServerApi();
    const { agencies } = await context.queryClient.ensureQueryData(
      serverApi.agency.myAgencies.queryOptions(),
    );
    const orgId = agencies[0]?.id;
    if (orgId) {
      await context.queryClient.ensureQueryData(
        serverApi.agency.listRoster.queryOptions({ input: { orgId } }),
      );
    }
  },
  component: RosterPage,
});

function RosterPage() {
  const queryClient = useQueryClient();
  const { data: mine } = useSuspenseQuery(api.agency.myAgencies.queryOptions());
  // The layout guard guarantees at least one agency, so index 0 is safe.
  const orgId = mine.agencies[0].id;

  const { data } = useSuspenseQuery(api.agency.listRoster.queryOptions({ input: { orgId } }));
  const { data: subscription } = useSuspenseQuery(api.subscription.current.queryOptions());

  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [removeTarget, setRemoveTarget] = useState<{ userId: string; name: string } | null>(null);

  const refresh = () =>
    queryClient.invalidateQueries({
      predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'agency',
    });

  const inviteMutation = useMutation({
    ...api.organization.invite.mutationOptions(),
    onSuccess: () => {
      toast.success(`Invite sent to ${email.trim()}`);
      setEmail('');
      setInviting(false);
      refresh();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Invite failed'),
  });

  const removeMutation = useMutation({
    ...api.organization.removeMember.mutationOptions(),
    onSuccess: () => {
      toast.success('Athlete removed from roster');
      setRemoveTarget(null);
      refresh();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Remove failed'),
  });

  const plan = subscription?.plan ?? 'free';
  const cap = SOFT_CAPS[plan] ?? SOFT_CAPS.free;
  const overCap = data.totals.athletes >= cap;

  const handleInvite = () => {
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Enter an email address');
      return;
    }
    // Athletes join as 'member'; staff seats are managed from
    // /settings/organizations where the full role picker lives.
    inviteMutation.mutate({ orgId, email: trimmed, role: 'member' });
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">{data.agency.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {data.totals.athletes} athlete{data.totals.athletes === 1 ? '' : 's'} ·{' '}
            {data.totals.deals} deal{data.totals.deals === 1 ? '' : 's'} ·{' '}
            {formatCurrency(data.totals.value)} tracked ·{' '}
            {formatCurrency(data.totals.commission)} est. commission
          </p>
        </div>
        {!inviting && <Button onClick={() => setInviting(true)}>Invite athlete</Button>}
      </div>

      {overCap && (
        <Card className="mb-4 border-amber-200 bg-amber-50">
          <CardContent className="py-3 text-sm text-amber-900">
            You're at {data.totals.athletes} athletes on the{' '}
            <span className="font-medium">{plan === 'free' ? 'free' : plan}</span> plan (suggested
            limit {cap}). Inviting still works — upgrade when you're ready for more room and the
            recruiting dataset.
          </CardContent>
        </Card>
      )}

      {inviting && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-base">Invite an athlete</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="athlete@school.edu"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                They'll get a one-click sign-in link. If they're new to NilBoard, an account is
                created for them.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setInviting(false);
                  setEmail('');
                }}
                disabled={inviteMutation.isPending}
              >
                Cancel
              </Button>
              <Button size="sm" onClick={handleInvite} disabled={inviteMutation.isPending}>
                {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {data.athletes.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm space-y-2">
            <p className="font-medium text-foreground">No athletes on your roster yet.</p>
            <p className="text-muted-foreground">
              Invite one by email — they'll land on a NilBoard account linked to your agency, and
              their deals show up here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground">
                <tr>
                  <Th>Athlete</Th>
                  <Th>Status</Th>
                  <Th align="right">Deals</Th>
                  <Th align="right">Tracked value</Th>
                  <Th align="right">Est. commission</Th>
                  <Th>Joined</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.athletes.map((a) => (
                  <tr key={a.userId} className="border-t">
                    <Td>
                      {/* Invited-but-not-yet-signed-in athletes have no
                          name on their stub user — show the email we
                          invited rather than an empty cell. */}
                      <Link
                        to="/agency/athletes/$athleteId"
                        params={{ athleteId: a.userId }}
                        className="font-medium text-primary hover:underline"
                      >
                        {a.name || a.email}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {[a.sport, a.school, a.classYear].filter(Boolean).join(' · ') || a.email}
                      </div>
                    </Td>
                    <Td>
                      <StatusPill status={a.status} />
                      {!a.hasNcaaId && a.status === 'active' && (
                        <div className="text-xs text-amber-700 mt-1">No NCAA ID</div>
                      )}
                    </Td>
                    <Td align="right">
                      {a.dealCount}
                      {a.dealCountExcluded > 0 && (
                        <div className="text-xs text-muted-foreground">
                          +{a.dealCountExcluded} pre-contract
                        </div>
                      )}
                    </Td>
                    <Td align="right">{formatCurrency(a.totalValue)}</Td>
                    <Td align="right">
                      {a.commissionPercent === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <>
                          {formatCurrency(a.commissionEarned)}
                          <div className="text-xs text-muted-foreground">
                            {a.commissionPercent}%
                          </div>
                        </>
                      )}
                    </Td>
                    <Td>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(a.joinedAt)}
                      </span>
                    </Td>
                    <Td align="right">
                      <button
                        type="button"
                        onClick={() =>
                          // A stub user invited by email has no name until
                          // they sign in, so fall back to the email for the
                          // confirm dialog rather than rendering "null".
                          setRemoveTarget({ userId: a.userId, name: a.name || a.email })
                        }
                        className="text-xs text-muted-foreground underline hover:text-foreground"
                      >
                        Remove
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <ProspectsSection orgId={orgId} />

      <AlertDialog open={removeTarget !== null} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {removeTarget?.name} from your roster?</AlertDialogTitle>
            <AlertDialogDescription>
              They keep their NilBoard account and every deal they own — you just stop managing
              them. You can invite them again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                removeTarget && removeMutation.mutate({ orgId, userId: removeTarget.userId })
              }
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Recruiting pipeline — prospects imported from the dataset (agent epic
 * PR 3). Deliberately NOT plan-gated: importing needs an agent plan, but
 * an agent's already-imported pipeline stays theirs if the plan lapses.
 *
 * The invite flow forces an email-confirm step: dataset emails are
 * unverified (mock ones are @fixture.usefoundry.dev by construction), so
 * the agent always sees and can correct the address before anything
 * sends. When the email can't be delivered (preview envs without Resend),
 * the one-click invite link is surfaced for manual sharing — same
 * fallback contract as `organization.invite`.
 */
function ProspectsSection({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const { data: prospects } = useQuery(api.agency.listProspects.queryOptions({ input: { orgId } }));

  const [inviteTarget, setInviteTarget] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [fallbackLink, setFallbackLink] = useState<{ prospectId: string; url: string } | null>(
    null,
  );

  const refresh = () =>
    queryClient.invalidateQueries({
      predicate: (q) => Array.isArray(q.queryKey?.[0]) && q.queryKey[0][0] === 'agency',
    });

  const inviteMutation = useMutation({
    ...api.agency.inviteProspect.mutationOptions(),
    onSuccess: (result) => {
      if (result.emailSent) {
        toast.success(`Invite sent — profile prefilled from the dataset.`);
        setFallbackLink(null);
      } else {
        toast.error('Invite created but the email failed — share their one-click link below.');
        setFallbackLink({ prospectId: result.prospectId, url: result.inviteUrl });
      }
      setInviteTarget(null);
      refresh();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Invite failed'),
  });

  const archiveMutation = useMutation({
    ...api.agency.updateProspect.mutationOptions(),
    onSuccess: () => {
      toast.success('Prospect archived');
      refresh();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Archive failed'),
  });

  if (!prospects || prospects.length === 0) return null;

  const isSyntheticEmail = (email: string | null) =>
    !!email && email.endsWith('@fixture.usefoundry.dev');

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-base">Recruiting pipeline</CardTitle>
        <p className="text-sm text-muted-foreground">
          Imported from the dataset. Confirm each athlete's email, then invite — their profile
          arrives prefilled.
        </p>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <Th>Prospect</Th>
              <Th align="right">Reach</Th>
              <Th align="right">NIL value</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {prospects.map((p) => (
              <Fragment key={p.id}>
                <tr className="border-t">
                  <Td>
                    <span className="font-medium">{p.name}</span>
                    <div className="text-xs text-muted-foreground">
                      {[p.sport, p.school].filter(Boolean).join(' · ')}
                    </div>
                  </Td>
                  <Td align="right">{formatReach(p.socialFollowers)}</Td>
                  <Td align="right">{formatMoneyCompact(p.nilValuation)}</Td>
                  <Td>
                    {p.status === 'invited' ? (
                      <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-900">
                        Invited {p.invitedAt ? formatDate(p.invitedAt) : ''}
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                        Prospect
                      </span>
                    )}
                  </Td>
                  <Td align="right">
                    <div className="flex gap-3 justify-end">
                      <button
                        type="button"
                        className="text-xs text-primary underline hover:no-underline"
                        onClick={() => {
                          setInviteTarget(p.id);
                          // Force the agent to look at a synthetic dataset
                          // address instead of one-click sending to it.
                          setInviteEmail(isSyntheticEmail(p.email) ? '' : (p.email ?? ''));
                        }}
                      >
                        {p.status === 'invited' ? 'Re-invite' : 'Invite'}
                      </button>
                      <button
                        type="button"
                        className="text-xs text-muted-foreground underline hover:text-foreground"
                        onClick={() =>
                          archiveMutation.mutate({ orgId, prospectId: p.id, status: 'archived' })
                        }
                      >
                        Archive
                      </button>
                    </div>
                  </Td>
                </tr>
                {inviteTarget === p.id && (
                  <tr className="border-t bg-muted/20">
                    <td colSpan={5} className="px-3 py-3">
                      <div className="flex items-end gap-2 flex-wrap">
                        <div className="grow max-w-sm">
                          <Label htmlFor={`prospect-email-${p.id}`}>
                            Confirm {p.name.split(' ')[0]}'s email
                          </Label>
                          <Input
                            id={`prospect-email-${p.id}`}
                            type="email"
                            placeholder="athlete@school.edu"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            autoFocus
                          />
                          {isSyntheticEmail(p.email) && (
                            <p className="text-xs text-amber-700 mt-1">
                              The dataset only had a placeholder address — enter their real
                              email.
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          disabled={inviteMutation.isPending || !inviteEmail.trim()}
                          onClick={() =>
                            inviteMutation.mutate({
                              orgId,
                              prospectId: p.id,
                              email: inviteEmail.trim(),
                            })
                          }
                        >
                          {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setInviteTarget(null)}
                          disabled={inviteMutation.isPending}
                        >
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                {fallbackLink?.prospectId === p.id && (
                  <tr className="border-t bg-amber-50/50">
                    <td colSpan={5} className="px-3 py-3">
                      <Label htmlFor={`invite-link-${p.id}`} className="text-xs">
                        One-click sign-in link (email delivery failed — share this directly)
                      </Label>
                      <div className="flex gap-2 mt-1 max-w-xl">
                        <Input id={`invite-link-${p.id}`} readOnly value={fallbackLink.url} />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard?.writeText(fallbackLink.url);
                            toast.success('Link copied');
                          }}
                        >
                          Copy
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }: { status: 'active' | 'invited' }) {
  const styles =
    status === 'active'
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-amber-50 text-amber-900 border-amber-200';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${styles}`}>
      {status === 'active' ? 'Active' : 'Invited'}
    </span>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-2 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}
      scope="col"
    >
      {children}
    </th>
  );
}

function Td({ children, align }: { children?: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <td className={`px-3 py-3 align-top ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {children}
    </td>
  );
}
